import React, { useRef, useState } from 'react';
import { Upload, Box } from 'lucide-react';

interface ModelUploadFieldProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (url: string) => void;
  onUpload: (file: File) => Promise<string>;
  /** Called after a successful upload – use to auto-save */
  onUploadComplete?: (url: string) => void;
}

export const ModelUploadField: React.FC<ModelUploadFieldProps> = ({
  label,
  hint,
  value,
  onChange,
  onUpload,
  onUploadComplete,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const url = await onUpload(file);
      onChange(url);
      onUploadComplete?.(url);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setError(msg);
      console.error('Upload failed:', err);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="jm-field">
      <label className="jm-label">
        <Box size={14} />
        {label}
      </label>
      {hint && <span className="jm-field-hint jm-field-hint-top">{hint}</span>}
      <div className="jm-model-upload">
        <input
          className="jm-input"
          value={value}
          onChange={e => { onChange(e.target.value); setError(null); }}
          placeholder="/models/bent.glb or paste URL"
        />
        <button
          type="button"
          className="jm-btn jm-btn-upload"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          title="Upload GLB file"
        >
          {uploading ? (
            <span className="jm-spin">⋯</span>
          ) : (
            <>
              <Upload size={14} />
              <span>Upload .glb</span>
            </>
          )}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".glb,model/gltf-binary"
        onChange={handleFileChange}
        hidden
      />
      {error && <span className="jm-field-error">{error}</span>}
    </div>
  );
};
